import {
  ActionIcon,
  Autocomplete,
  Button,
  Checkbox,
  Collapse,
  Group,
  NumberInput,
  Paper,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { isNotEmpty, useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { LarkFileListParams } from '@mygpt/app';

interface AppOption {
  value: string;
  label: string;
  secret: string;
}

interface LarkProps {
  onParamsChange: (params: LarkFileListParams) => void;
}

export interface LarkFormValues {
  name: string;
  sync_interval: number;
  app_id: string;
  app_secret: string;
  share_urls: Array<{
    url: string;
    recursive: boolean;
  }>;
  notification_emails: string;
}

export const Lark: React.FC<LarkProps> = ({ onParamsChange }) => {
  const { t } = useTranslation();
  const appOptions: AppOption[] = [
    // { value: 'app_id_1', label: 'App ID 1', secret: 'app_secret_1' },
  ];

  const form = useForm<LarkFormValues>({
    initialValues: {
      name: '',
      sync_interval: 1,
      app_id: '',
      app_secret: '',
      share_urls: [
        {
          url: '',
          recursive: true,
        },
      ],
      notification_emails: '',
    },
    validate: {
      name: isNotEmpty(t('create_v2.integrations.task_management.name_required')),
      app_id: isNotEmpty(t('create_v2.integrations.data_sources.lark.validation.app_id_required')),
      app_secret: isNotEmpty(t('create_v2.integrations.data_sources.lark.validation.app_secret_required')),
      share_urls: (value) => {
        if (value.length === 0 || value.every((item) => !item.url.trim())) {
          return t('create_v2.integrations.data_sources.lark.validation.share_url_required');
        }
        // 校验每个 URL 格式
        const urlPattern = /^https?:\/\/.+/;
        const errors = value.map((item) => {
          if (!item.url.trim()) {
            return t('create_v2.integrations.data_sources.lark.validation.share_url_input_required');
          }
          if (!urlPattern.test(item.url.trim())) {
            return t('create_v2.integrations.data_sources.lark.validation.share_url_invalid_format');
          }
          return null;
        });
        return errors.some((e) => e) ? errors : null;
      },
    },
  });

  useEffect(() => {
    const values = form.values;
    const params = {
      name: values.name,
      sync_interval: values.sync_interval,
      app_id: values.app_id,
      app_secret: values.app_secret,
      share_urls: values.share_urls
        .filter((input) => input.url.trim())
        .map((input) => ({
          url: input.url.trim(),
          recursive: input.recursive,
        })),
      notification_emails: values.notification_emails
        .split(/[\n\s,，]+/)
        .map((email) => email.trim())
        .filter((val) => /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(val)),
    };
    onParamsChange(params);
  }, [form.values, onParamsChange]);

  const addShareUrlInput = () => {
    form.insertListItem('share_urls', { url: '', recursive: true });
  };

  const removeShareUrlInput = (index: number) => {
    form.removeListItem('share_urls', index);
  };

  return (
    <Paper shadow="xs" p="md" className="bg-violet-100 p-4 mt-4 rounded-lg">
      <Stack>
        <TextInput
          label={t('create_v2.integrations.task_management.name')}
          placeholder={t('create_v2.integrations.task_management.name_placeholder')}
          required
          {...form.getInputProps('name')}
        />
        <Group grow>
          <Autocomplete
            label={t('create_v2.integrations.data_sources.lark.app_id')}
            placeholder={t('create_v2.integrations.data_sources.lark.app_id_placeholder')}
            data={appOptions.map((option) => option.value)}
            {...form.getInputProps('app_id')}
          />
          <Autocomplete
            label={t('create_v2.integrations.data_sources.lark.app_secret')}
            placeholder={t('create_v2.integrations.data_sources.lark.app_secret_placeholder')}
            data={appOptions.map((option) => option.secret)}
            {...form.getInputProps('app_secret')}
          />
        </Group>

        {form.values.share_urls.map((_, index) => (
          <Group key={index} align="center">
            <TextInput
              placeholder={t('create_v2.integrations.data_sources.lark.share_url_placeholder')}
              style={{ flexGrow: 1 }}
              {...form.getInputProps(`share_urls.${index}.url`)}
              required
            />
            <Checkbox
              label={t('create_v2.integrations.data_sources.lark.include_subfolders')}
              {...form.getInputProps(`share_urls.${index}.recursive`, { type: 'checkbox' })}
            />
            <ActionIcon
              size="sm"
              color="red"
              onClick={() => removeShareUrlInput(index)}
              disabled={form.values.share_urls.length === 1}
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Group>
        ))}

        <Button variant="light" onClick={addShareUrlInput}>
          <Group>
            <IconPlus size={14} />
            <span>{t('create_v2.integrations.data_sources.lark.add_share_url')}</span>
          </Group>
        </Button>
        {/* <Text onClick={toggle} size="sm" className="cursor-pointer">
          {t('create_v2.integrations.data_sources.lark.notification_email')} &gt;
        </Text> */}
        <Collapse in={false}>
          <Textarea
            placeholder={t('create_v2.integrations.data_sources.lark.notification_email_placeholder')}
            {...form.getInputProps('notification_emails')}
          />
        </Collapse>
        <Group align="center">
          <Text size="sm" fw={500}>
            {t('create_v2.integrations.task_management.sync_interval')}：
          </Text>
          <NumberInput min={1} defaultValue={1} style={{ width: '80px' }} {...form.getInputProps('sync_interval')} />
          <Text size="sm">{t('create_v2.integrations.task_management.sync_interval_unit_day')}</Text>
        </Group>
      </Stack>
    </Paper>
  );
};
